import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import ManifestService, { IManifestCountry } from '../services/ManifestService.ts';
import { useAuth } from './AuthContext.tsx';

interface ManifestContextProps {
  countries: IManifestCountry[];
  languages: Record<string, string>;
  isLoading: boolean;
  error: string | null;
  refreshManifest: () => Promise<void>;
}

const ManifestContext = createContext<ManifestContextProps | undefined>(undefined);

export const ManifestProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [countries, setCountries] = useState<IManifestCountry[]>([]);
  const [languages, setLanguages] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refreshManifest = async () => {
    if (!isAuthenticated) {
      setCountries([]);
      setLanguages({});
      setError(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const manifest = await ManifestService.getManifest(true);
      setCountries(manifest.countries || []);
      setLanguages(manifest.languages || {});
    } catch {
      setError('Failed to fetch manifest');
      setCountries([]);
      setLanguages({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthLoading) return;

    if (!isAuthenticated) {
      setCountries([]);
      setLanguages({});
      setError(null);
      setIsLoading(false);
      return;
    }

    refreshManifest();
  }, [isAuthenticated, isAuthLoading]);

  const value = useMemo(
    () => ({
      countries,
      languages,
      isLoading,
      error,
      refreshManifest,
    }),
    [countries, languages, isLoading, error]
  );

  return <ManifestContext.Provider value={value}>{children}</ManifestContext.Provider>;
};

export const useManifest = (): ManifestContextProps => {
  const context = useContext(ManifestContext);
  if (!context) {
    throw new Error('useManifest must be used within a ManifestProvider');
  }
  return context;
};
